/**
 * Whether this board can be trusted right now.
 *
 * A silently dead collector showing a reassuring zero is worse than no panel at
 * all, and this integration has three distinct ways to go quiet without anything
 * looking broken: the credential gets rejected, a request pool runs out, or the
 * circuit breaker opens after a 429 and suppresses every call for hours. In all
 * three the threat counts simply stop rising, which reads exactly like a good
 * week.
 *
 * So the pools, the breaker and the last successful poll are rendered as
 * prominently as the threat figures themselves — and when any of them is off,
 * the verdict says the numbers above are stale rather than leaving the reader to
 * infer it.
 *
 * Three pools rather than one, because they fail independently and for different
 * reasons: SIEM is spent by the five-minute poll, People and campaigns by the
 * once-a-day batch. "We are near the ceiling" is a different sentence depending
 * on which one it is, and only the pool view answers it.
 */

import { Activity, Icon, KeyRound, TriangleAlert } from "@norican/icons";
import { Alert, Badge, DescriptionList, Freshness, Meter, Typography } from "@norican/react";

import type { Verdict } from "@/components/board";
import type { SecurityHealth } from "@/lib/api";

import { PanelCard } from "./panel-card";
import {
  formatCount,
  formatDayLabel,
  formatDuration,
  formatPct,
  formatTimestamp,
  isPollStale,
  POLL_STATUS_LABEL,
  pollStatusTone,
  staleAfterMs,
} from "./status";

/** Pool usage above this is worth colouring — there must be retry headroom. */
const BUDGET_WARN_PCT = 60;
const BUDGET_DANGER_PCT = 85;

interface IntegrationHealthProps {
  /** The health block from `/security/overview`. */
  health: SecurityHealth;
}

export function IntegrationHealth({ health }: IntegrationHealthProps) {
  const budget = health.budget;
  const stale = isPollStale(health);
  const statusTone = pollStatusTone(health.last_status);

  return (
    <PanelCard
      title="Integration health"
      caption={POLL_STATUS_LABEL[health.last_status] ?? health.last_status}
      verdict={healthVerdict(health, stale)}
      note={
        <Typography variant="caption" tone="muted">
          The three pools draw on one shared TAP budget, so a second consumer cannot quietly spend
          this one&rsquo;s headroom. A 429 clears only as the oldest request ages out of the rolling{" "}
          {budget.window_hours}-hour window — not after a cooldown — which is why backing off here
          is measured in hours and why retrying sooner would extend the lockout rather than recover
          from it. Gaps older than {health.lookback_limit_days} days are unrecoverable: TAP will not
          serve them at any price.
        </Typography>
      }
    >
      <div className="flex flex-col gap-5">
        {budget.circuit_open && (
          <Alert
            tone="danger"
            icon={<Icon icon={TriangleAlert} size="sm" />}
            title="Collection is suspended — Proofpoint rate-limited the credential"
          >
            No request will be made for another {formatDuration(budget.retry_after_sec)} (until{" "}
            {formatTimestamp(budget.circuit_open_until)}), after{" "}
            {formatCount(budget.consecutive_rate_limits)} consecutive rate-limit responses. Every
            figure on this board is frozen as of the last successful poll.
          </Alert>
        )}

        {!budget.circuit_open && stale && (
          <Alert
            tone="warning"
            icon={<Icon icon={Activity} size="sm" />}
            title="No recent successful poll"
          >
            {health.last_success_at
              ? `The last successful collection was ${formatTimestamp(health.last_success_at)}. Treat the counts above as stale, not as calm.`
              : "This integration has never completed a successful collection, so every count above is an empty rollup rather than a measurement."}
          </Alert>
        )}

        {health.store_detail && (
          <Alert
            tone="danger"
            icon={<Icon icon={TriangleAlert} size="sm" />}
            title="The rollup store could not be read"
          >
            {health.store_detail} The zeros above are a read failure, not a quiet week.
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Freshness
            label="Proofpoint"
            since={health.last_success_at ?? undefined}
            staleAfter={staleAfterMs(health)}
            tone={health.last_success_at ? undefined : "negative"}
          />
          <Badge tone={statusTone} variant="subtle" size="sm">
            {POLL_STATUS_LABEL[health.last_status] ?? health.last_status}
          </Badge>
          {budget.circuit_open && (
            <Badge tone="danger" variant="solid" size="sm">
              circuit open
            </Badge>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <Typography variant="body-sm">
            TAP request pools — rolling {budget.window_hours}h, from one shared budget
          </Typography>
          <PoolMeter
            label="SIEM (message events)"
            used={budget.siem_used}
            budget={budget.siem_budget}
          />
          <PoolMeter
            label="People (individually targeted)"
            used={budget.people_used}
            budget={budget.people_budget}
          />
          <PoolMeter
            label="Campaigns"
            used={budget.campaign_used}
            budget={budget.campaign_budget}
          />
        </div>

        <DescriptionList
          orientation="vertical"
          columns={2}
          items={[
            {
              label: "Last successful poll",
              value: formatTimestamp(health.last_success_at),
            },
            {
              label: "Collected up to",
              value: formatTimestamp(health.last_interval_end),
            },
            {
              label: "SIEM poll interval",
              value: `${formatDuration(health.siem_poll_interval_sec)} — ${formatCount(
                Math.round(86_400 / health.siem_poll_interval_sec),
              )} requests/day`,
            },
            {
              label: "Recoverable lookback",
              value: `${health.lookback_limit_days} days`,
            },
            {
              label: "Consecutive rate limits",
              value: formatCount(budget.consecutive_rate_limits),
            },
            {
              label: "Last daily batch",
              value: health.last_daily_batch_day
                ? formatDayLabel(health.last_daily_batch_day)
                : "Never run",
            },
          ]}
        />

        <div className="border-border flex gap-3 rounded-md border p-3">
          <div className="pt-0.5">
            <Icon icon={KeyRound} size="sm" tone="info" />
          </div>
          <div className="flex flex-col gap-1">
            <Typography variant="label" as="span">
              Credential
            </Typography>
            <Typography variant="caption" tone="muted">
              {health.credential_detail}
            </Typography>
          </div>
        </div>
      </div>
    </PanelCard>
  );
}

interface PoolMeterProps {
  label: string;
  used: number;
  budget: number;
}

/** One TAP pool's consumption against its ceiling — a ratio against a limit (§2). */
function PoolMeter({ label, used, budget }: PoolMeterProps) {
  const pct = budget > 0 ? Math.min(100, (used / budget) * 100) : 0;
  const tone = pct >= BUDGET_DANGER_PCT ? "danger" : pct >= BUDGET_WARN_PCT ? "warning" : "accent";
  return (
    <Meter.Root
      value={pct}
      min={0}
      max={100}
      size="sm"
      tone={tone}
      aria-valuetext={`${label}: ${formatCount(used)} of ${formatCount(budget)} requests used, ${formatPct(pct, 0)}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <Meter.Label>{label}</Meter.Label>
        <span className="text-fg-muted text-sm tabular-nums">
          {formatCount(used)} / {formatCount(budget)}
        </span>
      </div>
      <Meter.Track>
        <Meter.Indicator />
      </Meter.Track>
    </Meter.Root>
  );
}

/** Whether the rest of the board can be believed right now. */
function healthVerdict(health: SecurityHealth, stale: boolean): Verdict {
  if (health.budget.circuit_open) {
    return {
      label: "Act",
      tone: "danger",
      text: "The circuit breaker is open, so every figure on this board is frozen at the last successful poll. Nothing above is a measurement until it clears.",
    };
  }
  if (health.store_detail) {
    return {
      label: "Act",
      tone: "danger",
      text: "The rollup store could not be read, so the zeros above are a read failure rather than a quiet week.",
    };
  }
  if (stale) {
    return {
      label: "Watch",
      tone: "warning",
      text: "No recent successful poll. Read the counts above as stale, not as calm.",
    };
  }
  return {
    label: "Healthy",
    tone: "success",
    text: "Collection is running inside its request budget, so the figures above are current. No decision needed.",
  };
}
